import React from 'react';
import { BathFixtureShell, Rect, Line, Curve } from './BathFixtureFrame';

/**
 * Toilet variants:
 *   - two-piece (default): separate tank rectangle on top + bowl
 *     silhouette below. Most common residential install.
 *   - one-piece: tank and bowl share a single curved silhouette with
 *     no horizontal seam between them.
 *   - wall-hung: floating bowl with no visible tank (concealed in
 *     wall). Renders only the bowl silhouette, baseline lifted off
 *     the floor.
 *
 * Bowl shape (elongated vs. round) is purely a length/width ratio in
 * plan view; in side elevation both bowls look essentially the same so
 * we ignore `bowlShape` here.
 *
 * Variant field: `obj.mountType === 'wall-hung'` is the only canonical
 * field in the current data model (see `elevationConstants.js`
 * lines 93+/115+). For one-piece vs. two-piece there is no field yet —
 * defaults to two-piece, callers can pass `obj.subtype === 'one-piece'`
 * or `obj.style === 'one-piece'` to override.
 */

export function resolveToiletVariant(obj = {}) {
  if (obj.mountType === 'wall-hung') return 'wall-hung';
  if (obj.subtype === 'one-piece' || obj.style === 'one-piece') {
    return 'one-piece';
  }
  if (obj.subtype === 'wall-hung' || obj.style === 'wall-hung') {
    return 'wall-hung';
  }
  return 'two-piece';
}

export function ToiletSchematic({
  widthMeters,
  heightMeters,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SideView
        view={view}
        depth={depthMeters}
        height={heightMeters}
        obj={obj}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return (
    <FrontView
      width={widthMeters}
      height={heightMeters}
      obj={obj}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

ToiletSchematic.supportsSideViews = true;

function FrontView({ width, height, obj, strokeColor, strokeWidth }) {
  const variant = resolveToiletVariant(obj);
  return (
    <BathFixtureShell width={width} height={height}>
      {variant === 'wall-hung' && (
        <WallHung
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      {variant === 'one-piece' && (
        <OnePiece
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      {variant === 'two-piece' && (
        <TwoPiece
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </BathFixtureShell>
  );
}

/**
 * Side elevation. The toilet's depth (front-to-back) becomes the SVG
 * horizontal axis. Convention: LEFT view places the back of the toilet
 * (tank) on the LEFT side (x=0) and the bowl extending FORWARD to the
 * right (x=depth). RIGHT view mirrors that. The flush lever is on the
 * side of the tank, near the front-top corner — asymmetric L vs. R.
 */
function SideView({ view, depth, height, obj, strokeColor, strokeWidth }) {
  const variant = resolveToiletVariant(obj);
  // No containing rectangle: unlike a boxy fixture, a toilet's side
  // silhouette IS the drawing — a frame around it reads as a crate.
  return (
    <BathFixtureShell width={depth} height={height}>
      <g
        transform={
          view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
        }
      >
        {variant === 'wall-hung' && (
          <WallHungSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        )}
        {variant === 'one-piece' && (
          <OnePieceSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        )}
        {variant === 'two-piece' && (
          <TwoPieceSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        )}
      </g>
    </BathFixtureShell>
  );
}

/**
 * Two-piece: tank rectangle at back-top, curved bowl extending forward,
 * pedestal base to floor. Tank back is flush with the rear wall (x=0
 * in the un-mirrored frame). Flush lever sits on the side of the tank
 * near the front edge.
 */
function TwoPieceSide({ depth, height, strokeColor, strokeWidth }) {
  // Proportions from the 3D `toiletRenderer`: a ~6\"-deep tank at the
  // back rising from the bowl rim to the top; the rim (seat) at ~14/30
  // of the height; and ONE oval bowl base running from the tank to the
  // front and straight down to the floor with a curved front — the old
  // stacked tank/band/box read as a mailbox on a crate.
  const IN = 0.0254;
  const tankD = Math.min(6 * IN, depth * 0.3);
  const rimY = height - Math.min(14 * IN * (height / (30 * IN)), height * 0.55);
  const seatT = Math.min(1 * IN, height * 0.05);
  const lidLineY = Math.min(1.6 * IN, height * 0.08);
  const bowlFront = depth - 1 * IN;
  const baseFrontAtFloor = depth * 0.58;
  const baseBackAtFloor = tankD + depth * 0.04;
  return (
    <g>
      {/* Tank — from the top down to the seat. */}
      <Rect
        x={0}
        y={0}
        width={tankD}
        height={rimY - seatT}
        rx={depth * 0.015}
        ry={depth * 0.015}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Tank lid seam */}
      <Line
        x1={0}
        y1={lidLineY}
        x2={tankD}
        y2={lidLineY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Seat + lid slab, riding the rim from the tank to the front. */}
      <Rect
        x={tankD}
        y={rimY - seatT}
        width={bowlFront - tankD}
        height={seatT}
        rx={seatT / 2}
        ry={seatT / 2}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Bowl base — one silhouette: out to the curved front, tapering
          back under the bowl to the foot. */}
      <Curve
        d={[
          `M ${tankD} ${rimY}`,
          `L ${bowlFront - depth * 0.1} ${rimY}`,
          `Q ${bowlFront} ${rimY} ${bowlFront} ${rimY + (height - rimY) * 0.24}`,
          `Q ${bowlFront} ${rimY + (height - rimY) * 0.6} ${depth * 0.72} ${rimY + (height - rimY) * 0.72}`,
          `Q ${depth * 0.6} ${rimY + (height - rimY) * 0.85} ${baseFrontAtFloor} ${height}`,
          `L ${baseBackAtFloor} ${height}`,
          `L ${tankD} ${rimY}`,
          'Z',
        ].join(' ')}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Flush lever on the tank's front face, near the top. */}
      <Rect
        x={tankD - depth * 0.01}
        y={lidLineY + height * 0.03}
        width={depth * 0.04}
        height={height * 0.02}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * One-piece: continuous silhouette from tank back through bowl down to
 * base. No visible seam between tank and bowl.
 */
function OnePieceSide({ depth, height, strokeColor, strokeWidth }) {
  const tankTopY = 0;
  const tankBottomY = height * 0.5;
  const bowlBottomY = height * 0.72;
  const baseY = height;
  const tankBackX = 0;
  const tankDepth = depth * 0.4;
  const bowlFrontX = depth;
  const baseBackX = depth * 0.1;
  const baseFrontX = depth * 0.85;
  const d = [
    `M ${tankBackX} ${tankTopY}`,
    `L ${tankBackX + tankDepth} ${tankTopY}`,
    `Q ${tankBackX + tankDepth + depth * 0.15} ${tankBottomY * 0.7} ${bowlFrontX} ${tankBottomY}`,
    `Q ${bowlFrontX} ${(tankBottomY + bowlBottomY) / 2} ${bowlFrontX - depth * 0.08} ${bowlBottomY}`,
    `L ${baseFrontX} ${bowlBottomY}`,
    `L ${baseFrontX} ${baseY}`,
    `L ${baseBackX} ${baseY}`,
    `L ${baseBackX} ${bowlBottomY}`,
    `L ${tankBackX} ${bowlBottomY}`,
    'Z',
  ].join(' ');
  // Lever stub on side of tank near front
  const leverY = height * 0.18;
  return (
    <g>
      <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />
      <Line
        x1={tankBackX + tankDepth * 0.7}
        y1={leverY}
        x2={tankBackX + tankDepth * 0.95}
        y2={leverY - height * 0.04}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Wall-hung: just the bowl floating off the floor. Back edge flush
 * with the wall (x=0). No floor base, no visible tank.
 */
function WallHungSide({ depth, height, strokeColor, strokeWidth }) {
  const bowlTopY = height * 0.32;
  const bowlBottomY = height * 0.7;
  const bowlBackX = 0;
  const bowlFrontX = depth;
  const d = [
    `M ${bowlBackX} ${bowlTopY}`,
    `L ${bowlFrontX} ${bowlTopY}`,
    `Q ${bowlFrontX} ${(bowlTopY + bowlBottomY) / 2} ${bowlFrontX - depth * 0.08} ${bowlBottomY}`,
    `L ${bowlBackX + depth * 0.05} ${bowlBottomY}`,
    `Q ${bowlBackX} ${(bowlTopY + bowlBottomY) / 2} ${bowlBackX} ${bowlTopY}`,
    'Z',
  ].join(' ');
  return <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />;
}

/**
 * Two-piece: tank rectangle on top, a wider seat/lid band (the visible
 * bowl rim) below the tank, then a slim pedestal connecting bowl to
 * floor. The 3D `toiletRenderer` builds a bowl base wider than the
 * pedestal cap, so the front silhouette has a "tank → seat band →
 * pedestal" three-tier read rather than just a curved blob between tank
 * and floor.
 */
function TwoPiece({ width, height, strokeColor, strokeWidth }) {
  // Proportions from the 3D `toiletRenderer`: the bowl rim (seat) sits at
  // ~14/30 of the height off the floor, the tank fills the space above it,
  // and the bowl base is a tapered oval that is WIDER at the floor than at
  // the rim — the old skinny 45%-wide pedestal read as a column.
  const seatTopY = height * 0.47;
  const seatH = height * 0.09;
  const tankW = width * 0.85;
  const tankX = (width - tankW) / 2;
  const tankH = seatTopY - height * 0.015; // small reveal above the seat band
  const baseTopY = seatTopY + seatH;
  const baseTopW = width * 0.8; // pinched under the seat…
  const baseBotW = width * 0.92; // …flaring to the floor, like the mesh
  return (
    <g>
      {/* Tank — narrower than the seat band below. */}
      <Rect
        x={tankX}
        y={0}
        width={tankW}
        height={tankH}
        rx={width * 0.02}
        ry={width * 0.02}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Seat / lid band — full bbox width, slightly rounded corners.
          This is the visible edge of the bowl rim + lid when viewed from
          the front. */}
      <Rect
        x={0}
        y={seatTopY}
        width={width}
        height={seatH}
        rx={width * 0.04}
        ry={width * 0.04}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Bowl base — tapered flank curving out to a wider foot. */}
      <Curve
        d={[
          `M ${(width - baseTopW) / 2} ${baseTopY}`,
          `L ${(width + baseTopW) / 2} ${baseTopY}`,
          `Q ${(width + baseTopW) / 2 + width * 0.02} ${(baseTopY + height) / 2} ${(width + baseBotW) / 2} ${height}`,
          `L ${(width - baseBotW) / 2} ${height}`,
          `Q ${(width - baseTopW) / 2 - width * 0.02} ${(baseTopY + height) / 2} ${(width - baseTopW) / 2} ${baseTopY}`,
          'Z',
        ].join(' ')}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * One-piece: single continuous silhouette from tank top down to floor.
 * No internal split line; the tank curves smoothly into the bowl.
 */
function OnePiece({ width, height, strokeColor, strokeWidth }) {
  const tankTopY = 0;
  const tankBottomY = height * 0.45;
  const bowlBottomY = height * 0.78;
  const baseY = height;
  const tankW = width * 0.92;
  const tankX = (width - tankW) / 2;
  const bowlW = width;
  const baseW = width * 0.55;
  const baseX = (width - baseW) / 2;
  const d = [
    `M ${tankX} ${tankTopY}`,
    `L ${tankX + tankW} ${tankTopY}`,
    `L ${tankX + tankW} ${tankBottomY}`,
    `Q ${width} ${tankBottomY} ${bowlW} ${bowlBottomY * 0.85}`,
    `L ${bowlW} ${bowlBottomY}`,
    `L ${baseX + baseW} ${bowlBottomY}`,
    `L ${baseX + baseW} ${baseY}`,
    `L ${baseX} ${baseY}`,
    `L ${baseX} ${bowlBottomY}`,
    `L 0 ${bowlBottomY}`,
    `L 0 ${bowlBottomY * 0.85}`,
    `Q 0 ${tankBottomY} ${tankX} ${tankBottomY}`,
    'Z',
  ].join(' ');
  return <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />;
}

/**
 * Wall-hung: only the bowl silhouette, lifted off the floor (tank is
 * concealed in the wall, not visible in elevation). Bowl floats with
 * its top at the typical 16–18" comfort-height seat level.
 */
function WallHung({ width, height, strokeColor, strokeWidth }) {
  const bowlTopY = height * 0.35;
  const bowlBottomY = height * 0.75;
  return (
    <BowlProfile
      width={width}
      y0={bowlTopY}
      y1={bowlBottomY}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

/**
 * Bowl side-elevation profile — flat top, tapered curved underside.
 * Used by both two-piece (with a separate tank above) and wall-hung
 * (alone, no tank).
 */
function BowlProfile({ width, y0, y1, strokeColor, strokeWidth }) {
  const bowlH = y1 - y0;
  const innerInset = width * 0.04;
  const d = [
    `M ${innerInset} ${y0}`,
    `L ${width - innerInset} ${y0}`,
    `Q ${width} ${y0 + bowlH * 0.4} ${width - innerInset * 2} ${y1}`,
    `L ${innerInset * 2} ${y1}`,
    `Q 0 ${y0 + bowlH * 0.4} ${innerInset} ${y0}`,
    'Z',
  ].join(' ');
  return <Curve d={d} strokeColor={strokeColor} strokeWidth={strokeWidth} />;
}
